import { createContext, useContext, type ReactNode } from 'react'
import { useFacilityOverview } from './index.js'
import type { FacilitySummary } from './index.js'

interface FacilityContextValue {
  /** How many facilities this person can see. */
  total: number
  /** The facility, when there is exactly one: the shell opens straight onto it. Null when there are none or several. */
  soleFacility: FacilitySummary | null
  isLoading: boolean
  isError: boolean
}

const FacilityContext = createContext<FacilityContextValue | undefined>(undefined)

// What the shell decides its layout from: no facilities (an empty state),
// one (its dashboard), or several (the list). It asks for the count and a
// single row, never the whole list - a large client has hundreds, and the
// screens that show facilities each load what they show.
export function FacilityProvider({ children }: { children: ReactNode }) {
  const query = useFacilityOverview()
  const total = query.data?.total ?? 0

  const value: FacilityContextValue = {
    total,
    soleFacility: total === 1 ? (query.data?.first ?? null) : null,
    isLoading: query.isPending,
    isError: query.isError,
  }

  return <FacilityContext.Provider value={value}>{children}</FacilityContext.Provider>
}

export function useFacilityContext(): FacilityContextValue {
  const context = useContext(FacilityContext)
  if (!context) {
    throw new Error('useFacilityContext must be used within a FacilityProvider')
  }
  return context
}
